import { createSelector } from '@reduxjs/toolkit'

import type { RootState } from '@/store/store'
import type { ViewType } from '@/types/user'
import { UserRole } from '@/types/auth.types'

// Base selectors
export const selectAuthState = (state: RootState) => state.auth
export const selectUser = (state: RootState) => state.auth.user
export const selectOrganization = (state: RootState) => state.auth.organization
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated
export const selectIsLoading = (state: RootState) => state.auth.isLoading
export const selectAuthError = (state: RootState) => state.auth.error
export const selectIsMockAuth = (state: RootState) => state.auth.isMockAuth

/**
 * Maps Django4Lyfe backend roles to frontend dashboard views
 * Following the hierarchical role system from the backend
 */
export const selectUserViewType = createSelector([selectUser], (user): ViewType | null => {
    if (!user || !user.role) return null

    // Handle nested role structure from API
    const roleValue = typeof user.role === 'object' ? user.role.role : user.role

    switch (roleValue) {
        // Top-level roles → HR Dashboard (full system access)
        case UserRole.OWNER: // Level 0: Organization owner
        case UserRole.ADMIN: // Level 1: System administrator
        case UserRole.HR_ADMIN: // Level 2: HR system administrator
        case UserRole.HR_MANAGER: // Level 3: HR department manager
            return 'hr'

        // Management roles → Manager Dashboard (team management)
        case UserRole.PEOPLE_MANAGER: // Level 4: People/team manager
            return 'manager'

        // Standard access → Employee Dashboard (self-service)
        case UserRole.EMPLOYEE: // Level 5: Standard employee
            return 'employee'

        // Read-only access → Employee Dashboard (limited features)
        case UserRole.GUEST: // Level 6: Guest/read-only access
            return 'employee' // Guest gets employee view with read-only restrictions

        default:
            return null
    }
})

// Get formatted user profile for display
export const selectUserProfile = createSelector([selectUser], (user) => {
    if (!user) return null

    const nameParts = user.full_name.split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    // Handle nested role structure and get display name
    const roleInfo = user.role
        ? typeof user.role === 'object'
            ? user.role
            : { role: user.role, display_name: user.role, scope_department: null }
        : null
    const roleDisplayName = roleInfo ? roleInfo.display_name || getRoleDisplayName(roleInfo.role) : 'Unknown'

    return {
        firstName,
        lastName,
        preferredName: firstName,
        email: user.email,
        joinDate: 'March 15, 2023', // Mock data - TODO: Get from user.created
        department: roleInfo?.scope_department || 'General', // From role scope or mock
        role: roleDisplayName,
        employeeId: 'EMP-2023-0542', // Mock data - TODO: Get from employee data
        lastLogin: '2 hours ago', // Mock data - TODO: Get from user.last_login_at
        avatarUrl: user.profile?.profile_image_url || null,
        fullName: user.full_name,
    }
})

// Helper function to get role display names
function getRoleDisplayName(role: string): string {
    switch (role) {
        case UserRole.OWNER:
            return 'Owner'
        case UserRole.ADMIN:
            return 'Administrator'
        case UserRole.HR_ADMIN:
            return 'HR Administrator'
        case UserRole.HR_MANAGER:
            return 'HR Manager'
        case UserRole.PEOPLE_MANAGER:
            return 'People Manager'
        case UserRole.EMPLOYEE:
            return 'Employee'
        case UserRole.GUEST:
            return 'Guest'
        default:
            return role // Fallback to raw role value
    }
}

// Get user display name (first name only)
export const selectUserDisplayName = createSelector([selectUser], (user) => {
    if (!user) return ''
    const firstName = user.full_name.split(' ')[0]
    return firstName
})

// Check if user has specific role
export const selectHasRole = (roles: string[]) =>
    createSelector([selectUser], (user) => {
        if (!user || !user.role) return false
        // Handle nested role structure from API
        const roleValue = typeof user.role === 'object' ? user.role.role : user.role
        return roles.includes(roleValue)
    })
